import { asBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import React, { ComponentProps } from 'react';
import ILink from '../../types/ILink';
import { ProfileImageSmall } from '../../types/api/ImageType';
import { Pagination, PaginationItem } from '../../_lib/components/Pagination/Pagination';
import { Table, TableBody, TableCell, TableHead, TableModifier, TableRow } from '../../components/organisms/Table';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { DateFormat, DateFormats } from '../../_lib/components/DateFormat';
import { useBlockContext } from '../../components/blocks/BlockContext';
import { ProfilePictureName } from '../../_lib/components/ProfilePictureName';
import IUrl from '../../types/IUrl';
import ContentMessage from '../../_lib/components/ContentMessage/ContentMessage';

type User = {
  id: string;
  profileLink: ILink;
  createdAt: string;
  profileImageUrl: ProfileImageSmall;
  personInfoCardUrl?: IUrl;
  status: 'pendingExternally' | 'completedExternally' | 'completed';
};

export type LatestRegistrationsProps = {
  users: User[];
  paginationItems: PaginationItem[];
};

export function LatestRegistrations({ users, paginationItems }: LatestRegistrationsProps) {
  const { t } = useSonTranslation('LatestRegistrations');
  const { uuid } = useBlockContext();
  const personLabel = t('labels.person');
  const stateLabel = t('labels.status');
  const dateLabel = t('labels.date');

  const renderRow = (user: User) => {
    return (
      <TableRow key={`registration-${user.id}`}>
        <TableCell dataHead={personLabel}>
          <ProfilePictureName
            profileLink={user.profileLink}
            profileName={user.profileLink.name}
            profileImageUrl={user.profileImageUrl}
            personInfoCardUrl={user.personInfoCardUrl}
          />
        </TableCell>
        <TableCell dataHead={stateLabel}>{t(`registrationState.${user.status}`)}</TableCell>
        <TableCell dataHead={dateLabel}>
          <DateFormat date={user.createdAt} format={DateFormats.DATE_TIME} />
        </TableCell>
      </TableRow>
    );
  };

  if (users.length > 0) {
    return (
      <>
        <Table caption={t('widgetTitle')}>
          <TableHead>
            <TableRow>
              <TableCell modifier={TableModifier.SWIDTH}>{personLabel}</TableCell>
              <TableCell>{stateLabel}</TableCell>
              <TableCell>{dateLabel}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{users.map(renderRow)}</TableBody>
        </Table>
        {paginationItems.length > 0 && <Pagination parentHash={uuid} pageItems={paginationItems} />}
      </>
    );
  } else {
    return <ContentMessage text={t('noRegistrationsAvailable')} />;
  }
}

export const LatestRegistrationsBlock = asBlock(LatestRegistrations, BlockType.latestRegistrations);
export type LatestRegistrationsBlock = ComponentProps<typeof LatestRegistrationsBlock>;
